import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Trash, Warning, CheckCircle, FilePdf, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReportImage from "@/components/ReportImage";

const fmt = (n, ccy = "USD") => {
    if (n == null || isNaN(n)) return "—";
    return `${ccy === "GBP" ? "£" : ccy === "ZWL" ? "Z$" : "$"}${Number(n).toLocaleString()}`;
};

export default function ReportDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/reports/${id}`)
            .then((r) => setReport(r.data))
            .catch((e) => setErr(e.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, [id]);

    const onDelete = async () => {
        if (!window.confirm("Delete this report? This cannot be undone.")) return;
        try {
            await api.delete(`/reports/${id}`);
            toast.success("Report deleted");
            navigate("/reports");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete");
        }
    };

    if (loading) return <div className="p-10 overline" data-testid="report-loading">Loading report…</div>;
    if (err) return <div className="p-10 text-neg" data-testid="report-error">{String(err)}</div>;
    if (!report) return null;

    const e = report.extracted || {};
    const k = e.kpis || {};
    const ccy = e.currency || "USD";

    return (
        <div className="p-8 lg:p-10 max-w-[1400px]" data-testid={`report-detail-${report.id}`}>
            <Link to="/reports" className="inline-flex items-center gap-1 text-xs overline mb-4" data-testid="back-link">
                <ArrowLeft size={14} /> Back to archive
            </Link>
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
                <div className="min-w-0">
                    <div className="overline mb-2">{report.company_name} · {report.report_type === "financial" ? "Financial" : "Daily Ops"} · {report.report_date}</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter leading-tight">
                        {e.headline || "Daily Report"}
                    </h1>
                    <p className="text-sm text-muted2 mt-2 max-w-2xl">{e.summary || report.summary}</p>
                </div>
                {user?.role === "owner" ? (
                    <div className="flex gap-2 flex-wrap">
                        <a href={`${API}/reports/${report.id}/export.pdf`} target="_blank" rel="noreferrer"
                           data-testid="export-pdf"
                           className="btn-secondary gap-2">
                            <FilePdf size={14} /> PDF
                        </a>
                        <a href={`${API}/reports/${report.id}/export.xlsx`} target="_blank" rel="noreferrer"
                           data-testid="export-xlsx"
                           className="btn-secondary gap-2">
                            <FileXls size={14} /> Excel
                        </a>
                        <button onClick={onDelete} data-testid="delete-report" className="btn-secondary gap-2 text-neg border-neg hover:bg-neg hover:text-paper">
                            <Trash size={14} /> Delete
                        </button>
                    </div>
                ) : (
                    <div className="flex gap-2">
                        <a href={`${API}/reports/${report.id}/export.pdf`} target="_blank" rel="noreferrer"
                           data-testid="export-pdf-tl"
                           className="btn-secondary gap-2"><FilePdf size={14} /> PDF</a>
                        <a href={`${API}/reports/${report.id}/export.xlsx`} target="_blank" rel="noreferrer"
                           data-testid="export-xlsx-tl"
                           className="btn-secondary gap-2"><FileXls size={14} /> Excel</a>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Stat label="Revenue" value={fmt(k.revenue, ccy)} tone="pos" />
                <Stat label="Expenses" value={fmt(k.expenses, ccy)} tone="neg" />
                <Stat label="Net" value={fmt(k.net, ccy)} tone={(k.net ?? 0) >= 0 ? "pos" : "neg"} />
                <Stat label="Deliveries" value={k.deliveries ?? "—"} />
                <Stat label="Units Produced" value={k.units_produced ?? "—"} />
                <Stat label="Units Sold" value={k.units_sold ?? "—"} />
                <Stat label="Headcount" value={k.headcount_present ?? "—"} />
                <Stat label="Downtime (h)" value={k.downtime_hours ?? "—"} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="lg:col-span-2 grid-card p-6">
                    <div className="overline mb-3">Line items</div>
                    {(e.line_items || []).length === 0 ? (
                        <div className="text-sm text-muted2">No line items extracted.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="line-items-table">
                            <thead>
                                <tr className="text-left border-b border-rule">
                                    <th className="overline py-2">Label</th>
                                    <th className="overline py-2">Category</th>
                                    <th className="overline py-2 text-right">Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {e.line_items.map((li, i) => (
                                    <tr key={i} className="border-b border-rule/50">
                                        <td className="py-2">{li.label}</td>
                                        <td className="py-2 text-muted2 text-xs uppercase tracking-wider">{li.category}</td>
                                        <td className="py-2 text-right data-num">{fmt(li.value, li.currency || ccy)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="grid-card p-6">
                    <div className="overline mb-3">Activities</div>
                    <ul className="space-y-2 text-sm">
                        {(e.activities || []).length === 0 ? <li className="text-muted2">—</li> :
                            e.activities.map((a, i) => (
                                <li key={i} className="flex gap-2"><CheckCircle size={14} className="text-pos shrink-0 mt-0.5" /><span>{a}</span></li>
                            ))}
                    </ul>
                    <div className="overline mt-5 mb-3">Issues</div>
                    <ul className="space-y-2 text-sm">
                        {(e.issues || []).length === 0 ? <li className="text-muted2">—</li> :
                            e.issues.map((it, i) => (
                                <li key={i} className="flex gap-2"><Warning size={14} className="text-warn shrink-0 mt-0.5" /><span>{it}</span></li>
                            ))}
                    </ul>
                    {e.weather ? <><div className="overline mt-5 mb-2">Weather</div><div className="text-sm">{e.weather}</div></> : null}
                    {(e.unclear || []).length > 0 ? (
                        <>
                            <div className="overline mt-5 mb-2 text-warn">Unclear / verify</div>
                            <ul className="space-y-1 text-xs text-muted2">
                                {e.unclear.map((u, i) => <li key={i}>· {u}</li>)}
                            </ul>
                        </>
                    ) : null}
                </div>
            </div>

            {report.images && report.images.length > 0 ? (
                <div className="grid-card p-6 mb-6">
                    <div className="overline mb-3">Source uploads ({report.images.length})</div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {report.images.map((img, i) => (
                            <div key={i} className="border border-rule rounded-sm overflow-hidden">
                                <ReportImage image={img} className="w-full h-40 object-cover" />
                                <div className="text-[10px] text-muted2 px-2 py-1 truncate" data-testid={`source-image-${i}`}>{img.filename}</div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {e.raw_text ? (
                <div className="grid-card p-6">
                    <div className="overline mb-3">Raw extracted text</div>
                    <pre className="whitespace-pre-wrap text-xs text-muted2 leading-relaxed font-mono">{e.raw_text}</pre>
                </div>
            ) : null}
        </div>
    );
}

function Stat({ label, value, tone = "neutral" }) {
    const c = tone === "pos" ? "text-pos" : tone === "neg" ? "text-neg" : "text-ink";
    return (
        <div className="grid-card p-4">
            <div className="overline">{label}</div>
            <div className={`data-num text-xl mt-1 ${c}`}>{value}</div>
        </div>
    );
}
